import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components';
import type {
  HistoryDateFilter,
  HistoryDateFilterItem,
  HistoryPageFilter,
} from '@/shared/types';
import { tv } from 'tailwind-variants';
import CalendarIcon from '@/shared/assets/calendar-icon.svg?react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon, XIcon } from 'lucide-react';
import {
  buildPeriodSelectOptions,
  useDateLocale,
  useDateModal,
} from '@/shared/lib';
import { useCallback, useMemo, useState } from 'react';
import { formatPeriodLabel } from '@/shared/utils';

type Props = {
  filters: HistoryPageFilter;
  onFiltersChange: (filters: HistoryPageFilter) => void;
  options: HistoryDateFilterItem[];
  onResetPeriod: () => void;
};

const classes = tv({
  slots: {
    wrapper: 'flex items-center gap-[0.8rem]',
    calendarIconWrapper:
      'w-[2.4rem] h-[2.4rem] flex items-center justify-center',
    calendarIcon: 'size-[2.4rem] text-primary',
    placeholder: 'headline4 text-primary',
    clearButton:
      'w-[2rem] h-[2rem] p-0 rounded-full flex items-center justify-center hover:bg-secondary-bg',
    clearButtonIcon: 'size-[2rem] text-secondary',
    selectTrigger:
      'headline4 hover:bg-stroke w-auto max-h-[4.2rem] h-[4.2rem] rounded-full bg-white px-[1.6rem] py-[0.9rem] cursor-pointer',
    selectContent: 'min-w-[23.6rem] bg-white rounded-[1.6rem]',
    selectContentViewport: 'min-w-[23.6rem]',
    selectItem: 'py-[1.6rem] px-[2.4rem] w-full cursor-pointer',
    selectItemTitle: 'headline4 w-full',
    chevronRightIcon: 'size-8 text-tertiary',
  },
});

const HistoryDateFilterSelect = ({
  filters,
  onFiltersChange,
  options,
  onResetPeriod,
}: Props) => {
  const { t } = useTranslation();
  const c = classes();
  const { dateLocale } = useDateLocale();
  const { openDateModal, closeDialog } = useDateModal();
  const [open, setOpen] = useState(false);

  const { datefilter, datefrom, dateto } = filters;

  const periodOptions = useMemo(
    () => buildPeriodSelectOptions(options),
    [options]
  );

  const updateDateFilter = useCallback(
    (value: HistoryDateFilter | undefined) =>
      onFiltersChange({ ...filters, datefilter: value }),
    [filters, onFiltersChange]
  );

  const updateCustomPeriod = useCallback(
    (dateFrom?: Date, dateTo?: Date) =>
      onFiltersChange({
        ...filters,
        datefilter: 'custom',
        datefrom: dateFrom,
        dateto: dateTo,
      }),
    [filters, onFiltersChange]
  );

  const openRangeModal = useCallback(() => {
    setOpen(false);
    openDateModal({
      onApply: (dateFrom?: Date, dateTo?: Date) => {
        updateCustomPeriod(dateFrom, dateTo);
        closeDialog();
      },
      onBack: closeDialog,
    });
  }, [openDateModal, updateCustomPeriod, closeDialog]);

  const handlePeriodSelect = useCallback(
    (value: HistoryDateFilter) => updateDateFilter(value),
    [updateDateFilter]
  );

  const handleClearDateFilter = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onResetPeriod();
    },
    [onResetPeriod]
  );

  return (
    <Select
      value={datefilter ?? ''}
      onValueChange={handlePeriodSelect}
      open={open}
      onOpenChange={setOpen}>
      <SelectTrigger
        size="md"
        className={c.selectTrigger()}
        withChevron={false}>
        <div className={c.wrapper()}>
          <div className={c.calendarIconWrapper()}>
            <CalendarIcon className={c.calendarIcon()} />
          </div>

          {datefilter === 'custom' ? (
            formatPeriodLabel({
              locale: dateLocale,
              dateFrom: datefrom,
              dateTo: dateto,
              defaultLabel: t('historyPage.dateFilterPlaceholder'),
            })
          ) : datefilter ? (
            <SelectValue />
          ) : (
            <div className={c.placeholder()}>
              {t('historyPage.dateFilterPlaceholder')}
            </div>
          )}

          {datefilter && (
            <Button
              variant="icon"
              onPointerDown={handleClearDateFilter}
              className={c.clearButton()}>
              <XIcon className={c.clearButtonIcon()} />
            </Button>
          )}
        </div>
      </SelectTrigger>

      <SelectContent
        align="end"
        className={c.selectContent()}
        classNameViewport={c.selectContentViewport()}>
        {periodOptions.map(({ value, name }) => (
          <SelectItem
            key={value}
            value={value ?? ''}
            className={c.selectItem()}
            showOptionCheckmark={false}>
            <span className={c.selectItemTitle()}>
              {value === 'custom' ? t('historyPage.customPeriod') : name}
            </span>
            {value === 'custom' && (
              <ChevronRightIcon className={c.chevronRightIcon()} />
            )}
          </SelectItem>
        ))}

        <SelectItem
          key="custom"
          value="custom"
          className={c.selectItem()}
          showOptionCheckmark={false}
          onPointerDown={openRangeModal}>
          <span className={c.selectItemTitle()}>
            {t('historyPage.customPeriod')}
          </span>
          <ChevronRightIcon className={c.chevronRightIcon()} />
        </SelectItem>
      </SelectContent>
    </Select>
  );
};

export { HistoryDateFilterSelect };
